import React from 'react';
import { graphql, Link } from 'gatsby';
import Layout from '../components/Layout';
import PageBanner from '../components/PageBanner';
import Section from '../components/Section';
import { Container, Row, Column } from '../components/Grid';
import Image from '../components/Image';
import Video from '../components/Video';
import Button from '../components/Button';
import AboutPartners from '../components/AboutPartners';
import './why-we-hate.scss';

const activities = [
  {
    videoSrc: 'https://app.discoveryeducation.com/learn/embedded-player/be2046d5-b84d-4958-a6ce-7cc7245f3938?utm_source=microsite&utm_medium=embed',
    title: 'Tribalism — Us Vs. Them',
    description:
      'In this activity students examine the individual and societal costs and benefits of tribalism (us versus them affiliations) and conduct an inquiry that helps them understand the role that social media plays in fueling tribalism. Through video and text, students learn that tribalism can take many forms and that individuals\' tribal affiliations can change over time and use secondary source materials to investigate the impact of social media tools and tactics on tribalism. Students\' work culminates in a collage to communicate ideas about how differences within their school community can be bridged.',
    extension:
      'As an extension, students can explore the role that tribalism played during the Holocaust and its impact on individuals through eyewitness testimonies.',
    downloadHref: '/pdf/Why-We-Hate-Tribalism-Classroom-Activity.pdf',
    downloadText: 'Download Activity',
  },
  {
    videoSrc: 'https://app.discoveryeducation.com/learn/embedded-player/4b91e68d-d0f7-4780-b620-09368f6c5bef?utm_source=microsite&utm_medium=embed',
    title: 'Biology of Hate',
    description:
      'In this activity, students investigate the biological origins of aggression and hate and consider whether human beings can learn not to hate. Following examination of several studies in infant cognition to understand how human nature contributes to moral judgements and negative feelings toward others, student then analyze the role that propaganda plays in fueling feelings of aggression and hatred, their exploration culminating in a media literacy activity to think critically about how media messages impact our perceptions and emotions.',
    extension:
      'As an extension, students can investigate the role that propaganda played in fueling the 1994 Genocide against the Tutsi in Rwanda.',
    downloadHref: '/pdf/Why-We-Hate-Activity-Biology-Of-Hate.pdf',
    downloadText: 'Download Activity',
  },
  {
    videoSrc: 'https://app.discoveryeducation.com/learn/embedded-player/03ee0cc3-2144-48c4-bc8a-ed891398b95f?utm_source=microsite&utm_medium=embed',
    title: 'Origins of Hate',
    description:
      'Examine the biological, psychological, and sociological geneses of hate by exploring animal behavior, brain science, and child development. Then dig into the phenomenon of bullying through powerful examples of young people who changed their mentality from hate to hope. Extend their exploration of hatred and human evolution by conducting research about genocides and viewing eyewitness accounts from USC Shoah Foundation\'s testimonies.',
    extension: null,
    downloadHref: '/pdf/Why-We-Hate-The-Origins-Of-Hate-Classroom-Activity.pdf',
    downloadText: 'Download Activity',
  },
];

const WhyWeHatePage = ({ data }) => {
  const page = data.pagesYaml;

  return (
    <Layout title={page.title} description={page.metaDescription}>
      <PageBanner title={page.title} />

      {/* ── Intro ──────────────────────────────────────────────────── */}
      <Section>
        <Container>
          <Row>
            <Column>
              <p className="wwh-subheading">
                Discovery Docuseries from Steven Spielberg and Alex Gibney
              </p>
              <p>
                In collaboration with Discovery Channel's global premiere of WHY WE HATE,
                a six-part docuseries that explores the reasons why humans hate, USC Shoah
                Foundation and Discovery Education have created a collection of powerful
                instructional resources that connect the important themes of the docuseries
                to classroom instruction.
              </p>
              <p>
                Support middle and high school students as they investigate human beings'
                propensity to hate and learn how to prevent it in their own lives. Once
                you've explored these resources, check out the{' '}
                <Link to="/virtual-field-trip/stronger-than-hate/" className="wwh-inline-link">
                  Our Stories are Stronger Than Hate Virtual Field Trip
                </Link>{' '}
                to take action with your students and make our world stronger than hate.
              </p>
            </Column>
          </Row>
        </Container>
      </Section>

      {/* ── Activity sections ───────────────────────────────────────── */}
      {activities.map((activity, i) => (
        <Section key={i} className={i % 2 === 1 ? 'wwh-activity-section--alt' : 'wwh-activity-section'}>
          <Container>
            <div className="wwh-activity">
              <div className="wwh-activity__video">
                <Video src={activity.videoSrc} title={activity.title} />
              </div>
              <div className="wwh-activity__body">
                <h3 className="wwh-activity__title">{activity.title}</h3>
                <p>{activity.description}</p>
                {activity.extension && <p className="wwh-activity__extension">{activity.extension}</p>}
                <Button
                  to={activity.downloadHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {activity.downloadText}
                </Button>
              </div>
            </div>
          </Container>
        </Section>
      ))}

      {/* ── Virtual Field Trip CTA ──────────────────────────────────── */}
      <Section className="wwh-vft-section">
        <Container>
          <Row>
            <Column>
              <h2 className="wwh-vft-section__heading">Virtual Field Trip</h2>
              <p className="wwh-vft-section__text">
                Take students on a voyage to hear first-hand stories from powerful voices
                to learn that stories have potential — potential to change the future.
              </p>
              <Button to="/virtual-field-trip/stronger-than-hate/">
                Watch the Virtual Field Trip
              </Button>
            </Column>
          </Row>
        </Container>
      </Section>

      <AboutPartners />
    </Layout>
  );
};

export default WhyWeHatePage;

export const query = graphql`
  query WhyWeHatePageQuery($id: String!) {
    pagesYaml(id: { eq: $id }) {
      slug
      path
      title
      metaDescription
    }
  }
`;
